// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import { PATIENT_TIMEZONE_EXTENSION_URL, setPatientTimezone } from '@polarissleep/shared';

export function getDeviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

function isValidTimezone(timezone: string): boolean {
  if (!timezone) {
    return false;
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function hasPatientTimezone(patient: Patient): boolean {
  return (
    patient.extension?.some(
      (extension) => extension.url === PATIENT_TIMEZONE_EXTENSION_URL && Boolean(extension.valueString)
    ) ?? false
  );
}

export async function ensurePatientTimezone(medplum: MedplumClient, patient: Patient): Promise<Patient> {
  if (hasPatientTimezone(patient)) {
    return patient;
  }

  const timezone = getDeviceTimezone();
  if (!isValidTimezone(timezone)) {
    return patient;
  }

  try {
    return await medplum.updateResource(setPatientTimezone(patient, timezone));
  } catch (error) {
    console.warn('[ensurePatientTimezone] Failed to persist patient timezone:', error);
    return patient;
  }
}
